import React, { useContext, useState, useCallback, useEffect, useMemo } from "react";
import GlobalContext from "../../context/GlobalContext";
import { getAttendanceAnalytics, getFinancialAnalytics } from "../../api/AnalyticsApi";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, Download, RefreshCw, Clock } from "lucide-react";
import { utils as xlsxUtils, writeFile } from "xlsx";

import { tr } from "../../i18n";
// ─── colour palette ────────────────────────────────────────────────────────
// Fixed-order categorical hues, validated for CVD separation + lightness band
// (blue kept as-is — it's the app's brand color; the rest snapped to pass).
const COLORS = ["#007DFF", "#1baf7a", "#eda100", "#008300", "#4a3aa7", "#e34948", "#e87ba4", "#eb6834"];

const CATEGORY_COLOR = {
  Attendance: "#007DFF",
  Leave:      "#008300",
  Overtime:   "#eb6834",
  Late:       "#eda100",
  Unknown:    "#adb5bd",
};

// Reserved status colors — only for "good/bad" state (KPI trend arrows),
// never reused as a category hue.
const STATUS_GOOD     = "#0ca30c";
const STATUS_CRITICAL = "#d03b3b";

// ─── helpers ───────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n ?? 0);

const fmtHours = (n) => `${(n ?? 0).toFixed(1)} hrs`;

// Format from local date components (not toISOString) — toISOString converts
// through UTC first, which silently shifts the date back a day in UTC+ zones
// like WIB/WITA/WIT.
const toLocalISODate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const DATE_PRESETS = [
  { id: "today",   labelKey: "analytics:preset_today",       range: () => { const t = new Date(); return { from: toLocalISODate(t), to: toLocalISODate(t) }; } },
  { id: "7d",       labelKey: "analytics:preset_last_7_days",  range: () => { const t = new Date(); return { from: toLocalISODate(addDays(t, -6)), to: toLocalISODate(t) }; } },
  { id: "30d",      labelKey: "analytics:preset_last_30_days", range: () => { const t = new Date(); return { from: toLocalISODate(addDays(t, -29)), to: toLocalISODate(t) }; } },
  { id: "thisMonth", labelKey: "analytics:preset_this_month",  range: () => { const t = new Date(); return { from: toLocalISODate(new Date(t.getFullYear(), t.getMonth(), 1)), to: toLocalISODate(t) }; } },
  { id: "lastMonth", labelKey: "analytics:preset_last_month",  range: () => { const t = new Date(); return { from: toLocalISODate(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: toLocalISODate(new Date(t.getFullYear(), t.getMonth(), 0)) }; } },
];

const getDefaultRange = () => DATE_PRESETS.find((p) => p.id === "30d").range();

// ─── sub-components ────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, change, icon, color = "#007DFF" }) {
  const isPos = change > 0;
  const isNeg = change < 0;
  return (
    <div
      className="card border-0 h-100"
      style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
    >
      <div className="card-body d-flex flex-column gap-1 p-3">
        <div className="d-flex justify-content-between align-items-start">
          <span className="text-muted" style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase" }}>
            {label}
          </span>
          <span
            className="d-flex align-items-center justify-content-center"
            style={{ width: 36, height: 36, borderRadius: 10, background: color + "18" }}
          >
            {icon}
          </span>
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: "#1a1a2e" }}>{value}</div>
        {sub && <div className="text-muted" style={{ fontSize: 12 }}>{sub}</div>}
        {change !== null && change !== undefined && (
          <div
            className="d-flex align-items-center gap-1 mt-1"
            style={{ fontSize: 12, fontWeight: 600, color: isPos ? STATUS_GOOD : isNeg ? STATUS_CRITICAL : "#6c757d" }}
          >
            {isPos ? <TrendingUp size={14} /> : isNeg ? <TrendingDown size={14} /> : <Minus size={14} />}
            {Math.abs(change)}{tr("analytics:vs_prev_period")}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h6 className="mb-3 fw-bold text-secondary d-flex align-items-center gap-2">
      <span style={{ width: 4, height: 18, background: "#007DFF", borderRadius: 2, display: "inline-block" }} />
      {children}
    </h6>
  );
}

const CustomTooltipCurrency = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded p-2 shadow-sm" style={{ fontSize: 12, minWidth: 180 }}>
      <div className="fw-bold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>
          {p.name}{tr("analytics:rp")}{" "} {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

// ─── Attendance Tab ─────────────────────────────────────────────────────────
function AttendanceAnalytics({ data, loading }) {
  if (loading && !data) return <AnalyticsSkeleton />;
  if (!data) return <EmptyState />;

  const { summary, by_category, daily_trend, by_department } = data;

  const allCategories = [...new Set(daily_trend.flatMap((d) => Object.keys(d).filter((k) => k !== "date")))];

  return (
    <div
      className="d-flex flex-column gap-4"
      style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s ease", pointerEvents: loading ? "none" : "auto" }}
    >
      {/* KPI cards */}
      <div className="row g-3">
        {[
          {
            label: tr("analytics:total_records"),
            value: fmt(summary.total_records),
            change: summary.period_change,
            icon: <i className="fas fa-clipboard-list" style={{ color: "#007DFF", fontSize: 16 }} />,
            color: "#007DFF",
          },
          {
            label: tr("analytics:late_arrivals"),
            value: fmt(summary.late_count),
            sub: summary.total_records > 0 ? `${((summary.late_count / summary.total_records) * 100).toFixed(1)}% of total` : null,
            icon: <i className="fas fa-clock" style={{ color: "#ffc107", fontSize: 16 }} />,
            color: "#ffc107",
          },
          {
            label: tr("analytics:avg_work_hours"),
            value: fmtHours(summary.avg_work_hours),
            sub: `Total ${fmtHours(summary.total_hours)}`,
            icon: <i className="fas fa-hourglass-half" style={{ color: "#28a745", fontSize: 16 }} />,
            color: "#28a745",
          },
          {
            label: tr("analytics:prev_period"),
            value: fmt(summary.prev_total),
            sub: "Records in previous period",
            icon: <i className="fas fa-history" style={{ color: "#6f42c1", fontSize: 16 }} />,
            color: "#6f42c1",
          },
        ].map((kpi, i) => (
          <div className="col-6 col-lg-3" key={i}>
            <KpiCard {...kpi} />
          </div>
        ))}
      </div>

      {/* Daily Trend + Category Pie */}
      <div className="row g-3">
        <div className="col-lg-8">
          <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <div className="card-body p-3">
              <SectionTitle>{tr("analytics:daily_attendance_trend")}</SectionTitle>
              {daily_trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={daily_trend} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
                    <defs>
                      {allCategories.map((cat, i) => (
                        <linearGradient key={cat} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={CATEGORY_COLOR[cat] ?? COLORS[i % COLORS.length]} stopOpacity={0.25} />
                          <stop offset="95%" stopColor={CATEGORY_COLOR[cat] ?? COLORS[i % COLORS.length]} stopOpacity={0}    />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {allCategories.map((cat, i) => (
                      <Area
                        key={cat}
                        type="monotone"
                        dataKey={cat}
                        stackId="1"
                        stroke={CATEGORY_COLOR[cat] ?? COLORS[i % COLORS.length]}
                        fill={`url(#grad-${i})`}
                        strokeWidth={2}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <NoChartData />
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <div className="card-body p-3">
              <SectionTitle>{tr("analytics:by_category")}</SectionTitle>
              {by_category.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={by_category}
                        dataKey="count"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        innerRadius={42}
                      >
                        {by_category.map((entry, i) => (
                          <Cell key={i} fill={CATEGORY_COLOR[entry.category] ?? COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="d-flex flex-column gap-1 mt-2">
                    {by_category.map((c, i) => (
                      <div key={i} className="d-flex justify-content-between align-items-center" style={{ fontSize: 12 }}>
                        <span className="d-flex align-items-center gap-1">
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: CATEGORY_COLOR[c.category] ?? COLORS[i % COLORS.length], display: "inline-block" }} />
                          {c.category}
                        </span>
                        <span className="fw-semibold">{fmt(c.count)} <span className="text-muted">({c.percentage}%)</span></span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <NoChartData />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* By Department */}
      {by_department.length > 0 && (
        <div className="card border-0" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <div className="card-body p-3">
            <SectionTitle>{tr("analytics:attendance_by_department")}</SectionTitle>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={by_department} margin={{ top: 4, right: 12, left: -10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="department" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {allCategories.map((cat, i) => (
                  <Bar key={cat} dataKey={cat} stackId="a" fill={CATEGORY_COLOR[cat] ?? COLORS[i % COLORS.length]} radius={i === allCategories.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Department table */}
      {by_department.length > 0 && (
        <div className="card border-0" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <div className="card-body p-3">
            <SectionTitle>{tr("analytics:department_breakdown")}</SectionTitle>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                <thead className="table-light">
                  <tr>
                    <th>{tr("shared:department")}</th>
                    <th className="text-end">{tr("analytics:total")}</th>
                    {allCategories.map((c) => <th key={c} className="text-end">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {by_department.map((row, i) => (
                    <tr key={i}>
                      <td className="fw-semibold">{row.department}</td>
                      <td className="text-end">{fmt(row.total)}</td>
                      {allCategories.map((c) => <td key={c} className="text-end text-muted">{fmt(row[c] ?? 0)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Financial Tab ──────────────────────────────────────────────────────────
function FinancialAnalytics({ data, loading }) {
  if (loading && !data) return <AnalyticsSkeleton />;
  if (!data) return <EmptyState />;

  const { summary, by_payroll, by_department, top_items } = data;

  const additionItems  = top_items.filter((i) => i.type !== "deduction");
  const deductionItems = top_items.filter((i) => i.type === "deduction");

  return (
    <div
      className="d-flex flex-column gap-4"
      style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s ease", pointerEvents: loading ? "none" : "auto" }}
    >
      {/* KPI cards */}
      <div className="row g-3">
        {[
          {
            label: tr("analytics:total_gross"),
            value: `Rp ${fmt(summary.total_gross)}`,
            sub: `${summary.payroll_count} payroll(s)`,
            change: summary.gross_change,
            icon: <i className="fas fa-money-bill-wave" style={{ color: "#007DFF", fontSize: 16 }} />,
            color: "#007DFF",
          },
          {
            label: tr("shared:total_deductions"),
            value: `Rp ${fmt(summary.total_deductions)}`,
            sub: summary.total_gross > 0 ? `${((summary.total_deductions / summary.total_gross) * 100).toFixed(1)}% of gross` : null,
            icon: <i className="fas fa-minus-circle" style={{ color: "#dc3545", fontSize: 16 }} />,
            color: "#dc3545",
          },
          {
            label: tr("analytics:total_take_home"),
            value: `Rp ${fmt(summary.total_take_home)}`,
            change: summary.take_home_change,
            icon: <i className="fas fa-hand-holding-usd" style={{ color: "#28a745", fontSize: 16 }} />,
            color: "#28a745",
          },
          {
            label: tr("analytics:employees_paid"),
            value: fmt(summary.total_employees),
            sub: `Across ${summary.payroll_count} payroll(s)`,
            icon: <i className="fas fa-users" style={{ color: "#6f42c1", fontSize: 16 }} />,
            color: "#6f42c1",
          },
        ].map((kpi, i) => (
          <div className="col-6 col-lg-3" key={i}>
            <KpiCard {...kpi} />
          </div>
        ))}
      </div>

      {/* Per-payroll bar + distribution donut */}
      <div className="row g-3">
        <div className="col-lg-8">
          <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <div className="card-body p-3">
              <SectionTitle>{tr("analytics:gross_vs_take_home_per_payroll")}</SectionTitle>
              {by_payroll.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={by_payroll} margin={{ top: 4, right: 12, left: 10, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="payroll_name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
                    <Tooltip content={<CustomTooltipCurrency />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="total_gross_salary"  name="Gross"      fill="#007DFF" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total_take_home_pay" name="Take-Home"  fill="#28a745" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total_deductions"    name="Deductions" fill="#dc3545" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <NoChartData />
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <div className="card-body p-3">
              <SectionTitle>{tr("analytics:distribution")}</SectionTitle>
              {summary.total_gross > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Take-Home",  value: summary.total_take_home },
                          { name: "Deductions", value: summary.total_deductions },
                        ]}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        innerRadius={42}
                      >
                        <Cell fill="#28a745" />
                        <Cell fill="#dc3545" />
                      </Pie>
                      <Tooltip formatter={(v) => `Rp ${fmt(v)}`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="d-flex flex-column gap-1 mt-2">
                    {[
                      { label: tr("analytics:take_home"),  val: summary.total_take_home,  color: "#28a745" },
                      { label: tr("analytics:deductions"), val: summary.total_deductions, color: "#dc3545" },
                    ].map((r, i) => (
                      <div key={i} className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                        <span className="d-flex align-items-center gap-1">
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: r.color, display: "inline-block" }} />
                          {r.label}
                        </span>
                        <span className="fw-semibold">Rp {fmt(r.val)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <NoChartData />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* By Department */}
      {by_department.length > 0 && (
        <div className="row g-3">
          <div className="col-lg-7">
            <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <div className="card-body p-3">
                <SectionTitle>{tr("analytics:financial_by_department")}</SectionTitle>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={by_department} layout="vertical" margin={{ top: 4, right: 30, left: 80, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
                    <YAxis dataKey="department" type="category" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip content={<CustomTooltipCurrency />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="total_gross"     name="Gross"     fill="#007DFF" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="total_take_home" name="Take-Home" fill="#28a745" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <div className="card-body p-3">
                <SectionTitle>{tr("analytics:department_table")}</SectionTitle>
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: 12 }}>
                    <thead className="table-light">
                      <tr>
                        <th>{tr("shared:department")}</th>
                        <th className="text-end">{tr("analytics:emp")}</th>
                        <th className="text-end">{tr("analytics:take_home")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {by_department.map((row, i) => (
                        <tr key={i}>
                          <td className="fw-semibold">{row.department}</td>
                          <td className="text-end text-muted">{row.employee_count}</td>
                          <td className="text-end">Rp {fmt(row.total_take_home)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Items */}
      {top_items.length > 0 && (
        <div className="row g-3">
          {[
            { title: tr("analytics:top_additions"), items: additionItems.slice(0, 8), color: "#28a745" },
            { title: tr("analytics:top_deductions"), items: deductionItems.slice(0, 8), color: "#dc3545" },
          ].map((section, si) =>
            section.items.length > 0 ? (
              <div className="col-lg-6" key={si}>
                <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                  <div className="card-body p-3">
                    <SectionTitle>{section.title}</SectionTitle>
                    <div className="d-flex flex-column gap-2">
                      {section.items.map((item, i) => {
                        const maxVal = section.items[0]?.total ?? 1;
                        const pct    = Math.round((item.total / maxVal) * 100);
                        return (
                          <div key={i}>
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: 12 }}>
                              <span className="fw-semibold text-truncate" style={{ maxWidth: "60%" }}>{item.item_name}</span>
                              <span className="text-muted">Rp {fmt(item.total)}</span>
                            </div>
                            <div className="progress" style={{ height: 6, borderRadius: 4 }}>
                              <div
                                className="progress-bar"
                                style={{ width: `${pct}%`, background: section.color, borderRadius: 4 }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>
      )}

      {/* Per-payroll detail table */}
      {by_payroll.length > 0 && (
        <div className="card border-0" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <div className="card-body p-3">
            <SectionTitle>{tr("analytics:payroll_detail")}</SectionTitle>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                <thead className="table-light">
                  <tr>
                    <th>{tr("shared:payroll_name")}</th>
                    <th>{tr("analytics:period")}</th>
                    <th className="text-end">{tr("analytics:employees")}</th>
                    <th className="text-end">{tr("analytics:gross")}</th>
                    <th className="text-end">{tr("analytics:deductions")}</th>
                    <th className="text-end">{tr("analytics:take_home")}</th>
                    <th className="text-center">{tr("shared:status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {by_payroll.map((row, i) => (
                    <tr key={i}>
                      <td className="fw-semibold">{row.payroll_name}</td>
                      <td className="text-muted" style={{ whiteSpace: "nowrap" }}>
                        {row.period_from} → {row.period_to}
                      </td>
                      <td className="text-end">{row.total_employee}</td>
                      <td className="text-end">Rp {fmt(row.total_gross_salary)}</td>
                      <td className="text-end text-danger">Rp {fmt(row.total_deductions)}</td>
                      <td className="text-end text-success fw-semibold">Rp {fmt(row.total_take_home_pay)}</td>
                      <td className="text-center">
                        <span className={`badge ${row.status === "published" ? "bg-success" : "bg-warning text-dark"}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Utility micro-components ───────────────────────────────────────────────

// Skeleton loading state — shown only on the first load (no data to hold onto
// yet). Refetches (changing dates, clicking Load) instead dim the existing
// content in place, so the layout never flashes/jumps.
function SkeletonBox({ width = "100%", height = 14, radius = 6, style }) {
  return <div className="az-skel" style={{ width, height, borderRadius: radius, ...style }} />;
}

function KpiCardSkeleton() {
  return (
    <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
      <div className="card-body d-flex flex-column gap-2 p-3">
        <div className="d-flex justify-content-between align-items-start">
          <SkeletonBox width={70} height={10} />
          <SkeletonBox width={36} height={36} radius={10} />
        </div>
        <SkeletonBox width="60%" height={24} style={{ marginTop: 4 }} />
        <SkeletonBox width="40%" height={10} />
      </div>
    </div>
  );
}

function ChartCardSkeleton({ height = 260 }) {
  return (
    <div className="card border-0 h-100" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
      <div className="card-body p-3">
        <SkeletonBox width={160} height={14} style={{ marginBottom: 16 }} />
        <SkeletonBox height={height} />
      </div>
    </div>
  );
}

function TableCardSkeleton({ rows = 5 }) {
  return (
    <div className="card border-0" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
      <div className="card-body p-3">
        <SkeletonBox width={160} height={14} style={{ marginBottom: 16 }} />
        <div className="d-flex flex-column gap-2">
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonBox key={i} height={28} />
          ))}
        </div>
      </div>
    </div>
  );
}

function AnalyticsSkeleton() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="row g-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="col-6 col-lg-3" key={i}>
            <KpiCardSkeleton />
          </div>
        ))}
      </div>
      <div className="row g-3">
        <div className="col-lg-8">
          <ChartCardSkeleton height={260} />
        </div>
        <div className="col-lg-4">
          <ChartCardSkeleton height={180} />
        </div>
      </div>
      <TableCardSkeleton rows={5} />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-5 text-muted">
      <i className="fas fa-chart-bar fa-3x mb-3" style={{ opacity: 0.3 }} />
      <p className="mb-0">{tr("analytics:no_data_recorded_for_this_period")}</p>
    </div>
  );
}

function NoChartData() {
  return (
    <div className="d-flex align-items-center justify-content-center text-muted py-4" style={{ height: 180, fontSize: 13 }}>
      <i className="fas fa-inbox me-2" />
      {tr("analytics:no_data")}
    </div>
  );
}

// ─── Export helpers ─────────────────────────────────────────────────────────
function exportAttendance(data, dateFrom, dateTo) {
  if (!data) return;
  const wb = xlsxUtils.book_new();

  const summaryRows = [
    ["Metric", "Value"],
    ["Total Records", data.summary.total_records],
    ["Late Count", data.summary.late_count],
    ["Avg Work Hours", data.summary.avg_work_hours],
    ["Total Hours", data.summary.total_hours],
    ["Period Change (%)", data.summary.period_change ?? "N/A"],
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(summaryRows), "Summary");

  if (data.by_category.length) {
    const rows = [["Category", "Count", "Percentage"], ...data.by_category.map((r) => [r.category, r.count, r.percentage + "%"])];
    xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(rows), "By Category");
  }

  if (data.daily_trend.length) {
    const keys = Object.keys(data.daily_trend[0]);
    const rows = [keys, ...data.daily_trend.map((r) => keys.map((k) => r[k] ?? 0))];
    xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(rows), "Daily Trend");
  }

  if (data.by_department.length) {
    const keys = Object.keys(data.by_department[0]);
    const rows = [keys, ...data.by_department.map((r) => keys.map((k) => r[k] ?? 0))];
    xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(rows), "By Department");
  }

  writeFile(wb, `Attendance_Analytics_${dateFrom}_${dateTo}.xlsx`);
}

function exportFinancial(data, dateFrom, dateTo) {
  if (!data) return;
  const wb = xlsxUtils.book_new();

  const summaryRows = [
    ["Metric", "Value"],
    ["Payroll Count", data.summary.payroll_count],
    ["Total Employees", data.summary.total_employees],
    ["Total Gross", data.summary.total_gross],
    ["Total Deductions", data.summary.total_deductions],
    ["Total Take-Home", data.summary.total_take_home],
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(summaryRows), "Summary");

  if (data.by_payroll.length) {
    const rows = [
      ["Payroll Name", "Period From", "Period To", "Employees", "Gross", "Deductions", "Take-Home", "Status"],
      ...data.by_payroll.map((r) => [r.payroll_name, r.period_from, r.period_to, r.total_employee, r.total_gross_salary, r.total_deductions, r.total_take_home_pay, r.status]),
    ];
    xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(rows), "By Payroll");
  }

  if (data.by_department.length) {
    const rows = [
      ["Department", "Employees", "Total Gross", "Total Deductions", "Total Take-Home", "Avg Hours"],
      ...data.by_department.map((r) => [r.department, r.employee_count, r.total_gross, r.total_deductions, r.total_take_home, r.avg_hours]),
    ];
    xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(rows), "By Department");
  }

  if (data.top_items.length) {
    const rows = [
      ["Item Name", "Group", "Type", "Total", "Count"],
      ...data.top_items.map((r) => [r.item_name, r.group_name, r.type, r.total, r.count]),
    ];
    xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(rows), "Payroll Items");
  }

  writeFile(wb, `Financial_Analytics_${dateFrom}_${dateTo}.xlsx`);
}

// ─── Main page ──────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const { user } = useContext(GlobalContext);
  const companyId = user?.workspace?.company_id;

  const [activeTab, setActiveTab] = useState("attendance");

  const defaultRange = useMemo(() => getDefaultRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo,   setDateTo]   = useState(defaultRange.to);

  const [attendanceData, setAttendanceData] = useState(null);
  const [financialData,  setFinancialData]  = useState(null);

  const [loadingAtt, setLoadingAtt] = useState(false);
  const [loadingFin, setLoadingFin] = useState(false);

  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Accepts an explicit range so presets can trigger a load with the new
  // dates immediately, without waiting on the setState/re-render cycle.
  const loadAll = useCallback(async (range) => {
    const from = range?.from ?? dateFrom;
    const to   = range?.to   ?? dateTo;
    if (!companyId) return;
    setError(null);
    setLoadingAtt(true);
    setLoadingFin(true);

    const [attRes, finRes] = await Promise.all([
      getAttendanceAnalytics({ companyId, dateFrom: from, dateTo: to }),
      getFinancialAnalytics({ companyId, dateFrom: from, dateTo: to }),
    ]);

    setLoadingAtt(false);
    setLoadingFin(false);
    setLastUpdated(new Date());

    if (attRes?.status === 200) {
      setAttendanceData(attRes.data);
    } else {
      setError(attRes?.message ?? tr("analytics:failed_to_load_attendance_analytics"));
    }

    if (finRes?.status === 200) {
      setFinancialData(finRes.data);
    } else if (!error) {
      setError(finRes?.message ?? tr("analytics:failed_to_load_financial_analytics"));
    }
  }, [companyId, dateFrom, dateTo, error]);

  // Auto-load once the workspace is known — no need to click Load for the
  // default range on first visit.
  useEffect(() => {
    if (companyId) loadAll({ from: defaultRange.from, to: defaultRange.to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const applyPreset = (preset) => {
    const range = preset.range();
    setDateFrom(range.from);
    setDateTo(range.to);
    loadAll(range);
  };

  const activePresetId = useMemo(() => {
    const match = DATE_PRESETS.find((p) => {
      const r = p.range();
      return r.from === dateFrom && r.to === dateTo;
    });
    return match?.id ?? null;
  }, [dateFrom, dateTo]);

  const handleExport = () => {
    if (activeTab === "attendance") exportAttendance(attendanceData, dateFrom, dateTo);
    else exportFinancial(financialData, dateFrom, dateTo);
  };

  const isLoading = loadingAtt || loadingFin;

  return (
    <div style={{ padding: "0 0 60px" }}>
      <style>{`
        @keyframes azShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
        .az-skel {
          background: linear-gradient(90deg, #eef0f3 25%, #f8f9fb 37%, #eef0f3 63%);
          background-size: 400px 100%;
          animation: azShimmer 1.4s ease-in-out infinite;
        }
        .az-preset-btn { transition: background 0.15s ease, color 0.15s ease; }
        @keyframes azSpin { to { transform: rotate(360deg); } }
        .spin { animation: azSpin 0.9s linear infinite; }
      `}</style>

      {/* ── Page header ── */}
      <div
        className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3 p-3 rounded-3"
        style={{ background: "linear-gradient(135deg, #007DFF 0%, #0056b3 100%)", color: "#fff" }}
      >
        <div>
          <h4 className="mb-0 fw-bold">
            <i className="fas fa-chart-line me-2" />
            {tr("common:menu.analytics")}
          </h4>
          <small style={{ opacity: 0.8 }}>{tr("analytics:attendance_financial_insights_for_your_organizat")}</small>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <input
            type="date"
            className="form-control form-control-sm"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ width: 140 }}
          />
          <span style={{ opacity: 0.7 }}>→</span>
          <input
            type="date"
            className="form-control form-control-sm"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ width: 140 }}
          />
          <button className="btn btn-light btn-sm d-flex align-items-center gap-1 fw-semibold" onClick={() => loadAll()}>
            <RefreshCw size={14} className={isLoading ? "spin" : ""} />
            {tr("analytics:load")}
          </button>
          <button
            className="btn btn-outline-light btn-sm d-flex align-items-center gap-1"
            onClick={handleExport}
            disabled={activeTab === "attendance" ? !attendanceData : !financialData}
          >
            <Download size={14} />
            {tr("analytics:export")}
          </button>
        </div>
      </div>

      {/* ── Date presets + last updated ── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div className="d-flex flex-wrap gap-2">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              className="az-preset-btn btn btn-sm"
              style={{
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                padding: "4px 14px",
                background: activePresetId === preset.id ? "#0d6ef0" : "#eef5ff",
                color: activePresetId === preset.id ? "#fff" : "#0d6ef0",
                border: "none",
              }}
              onClick={() => applyPreset(preset)}
            >
              {tr(preset.labelKey)}
            </button>
          ))}
        </div>
        {lastUpdated && (
          <small className="text-muted d-flex align-items-center gap-1">
            <Clock size={12} />
            {tr("analytics:updated")}{" "} {lastUpdated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
          </small>
        )}
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-3">
          <i className="fas fa-exclamation-triangle" />
          {error}
        </div>
      )}

      {/* ── Tabs ── */}
      <ul className="nav nav-pills mb-4 gap-1">
        {[
          { id: "attendance", label: tr("common:menu.attendance"), icon: "fas fa-calendar-check" },
          { id: "financial",  label: tr("analytics:financial"),  icon: "fas fa-coins" },
        ].map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button
              className={`nav-link d-flex align-items-center gap-2 ${activeTab === tab.id ? "active" : ""}`}
              style={{
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                background: activeTab === tab.id ? "#007DFF" : "transparent",
                color: activeTab === tab.id ? "#fff" : "#6c757d",
                border: activeTab === tab.id ? "none" : "1px solid #dee2e6",
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ── Content ── */}
      {activeTab === "attendance" && (
        <AttendanceAnalytics data={attendanceData} loading={loadingAtt} />
      )}
      {activeTab === "financial" && (
        <FinancialAnalytics data={financialData} loading={loadingFin} />
      )}
    </div>
  );
}
